import { useState, useMemo, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { useQuote } from '@/context/QuoteContext';

import productsRaw from '@/data/products.json';
import duratilesRaw from '@/data/duratiles.json';
import quaRaw from '@/data/qua.json';
import vitrafixRaw from '@/data/vitrafix.json';
import kalekimRaw from '@/data/kalekim.json';

// Minimal interface for raw JSON
interface RawWithImages {
  id: string | number;
  title: string;
  images?: string[];
  image?: string;
  category?: string;
  collection?: string;
  seriesName?: string;
}

export interface SimpleProduct {
  id: string;
  title: string;
  image: string;
  brand: string;
  category: string;
}

const BRAND_NAME_MAP: Record<string, string> = {
  creavit: 'Creavit',
  duratiles: 'Duratiles',
  qua: 'QUA',
  vitrafix: 'VitraFix',
  kalekim: 'Kalekim',
};

// Flatten global products
function getAllProducts(): SimpleProduct[] {
  const all: SimpleProduct[] = [];

  for (const p of productsRaw as RawWithImages[]) {
    const img = (p.images && p.images[0]) || '';
    if (!img) continue;
    all.push({ id: `crv-${p.id}`, title: p.title, image: img, brand: 'creavit', category: p.category || '' });
  }

  for (const p of duratilesRaw as RawWithImages[]) {
    const img = p.image || (p.images && p.images[0]) || '';
    all.push({ id: `drt-${p.id}`, title: p.title, image: img, brand: 'duratiles', category: p.collection || p.seriesName || '' });
  }

  for (const p of quaRaw as RawWithImages[]) {
    const img = (p.images && p.images[0]) || '';
    all.push({ id: `qua-${p.id}`, title: p.title, image: img, brand: 'qua', category: p.category || '' });
  }

  for (const p of vitrafixRaw as RawWithImages[]) {
    const img = (p.images && p.images[0]) || '';
    all.push({ id: `vfx-${p.id}`, title: p.title, image: img, brand: 'vitrafix', category: p.category || '' });
  }

  for (const p of kalekimRaw as RawWithImages[]) {
    const img = (p.images && p.images[0]) || '';
    all.push({ id: `klk-${p.id}`, title: p.title, image: img, brand: 'kalekim', category: p.category || '' });
  }

  return all;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export function GlobalSearchModal({ isOpen, onClose }: Props) {
  const [query, setQuery] = useState('');
  const products = useMemo(() => getAllProducts(), []);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const { toggle, has, openDrawer, count } = useQuote();

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setQuery('');
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const filtered = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return products
      .filter(p => p.title.toLowerCase().includes(q) || p.brand.toLowerCase().includes(q))
      .slice(0, 50);
  }, [query, products]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex flex-col items-center justify-center p-4 backdrop-blur-md bg-black/40" onClick={onClose}>
      <div 
        className="w-full max-w-2xl animate-in fade-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* The singular elegant spotlight search bar */}
        <div className="flex items-center bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 px-5 py-4">
          <Search className="w-7 h-7 text-gray-400 shrink-0 mr-4" />
          <input
            ref={inputRef}
            type="text"
            className="flex-1 text-xl outline-none bg-transparent text-gray-800 placeholder:text-gray-400 font-medium"
            placeholder="Malzeme adını yazın..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query.trim() && (
             <button 
               onClick={() => setQuery('')}
               className="shrink-0 p-2 ml-2 rounded-full hover:bg-black/5 text-gray-400 transition-colors"
             >
               <X className="w-6 h-6" />
             </button>
          )}
        </div>

        {/* Results Dropdown */}
        {query.trim() && filtered.length > 0 && (
          <div className="mt-2 bg-white rounded-xl shadow-2xl ring-1 ring-black/5 overflow-hidden flex flex-col max-h-[50vh]">
            <div className="flex-1 overflow-y-auto w-full custom-scrollbar p-2 space-y-1">
              {filtered.map(product => {
                const inQuote = has(product.id);
                return (
                  <div 
                    key={product.id} 
                    onClick={() => toggle({
                      id: product.id,
                      title: product.title,
                      image: product.image,
                      brand: BRAND_NAME_MAP[product.brand] || product.brand,
                      category: product.category,
                    })}
                    className="flex items-center p-3 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer group"
                  >
                    {/* Img */}
                    <div className="w-12 h-12 bg-white border border-gray-200 rounded-md shrink-0 mr-4 overflow-hidden flex items-center justify-center">
                      <img src={product.image} alt={product.title} className="max-w-full max-h-full object-contain mix-blend-multiply" />
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0 pr-4">
                      <h4 className="text-gray-900 font-medium truncate text-sm">{product.title}</h4>
                      <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500 mt-0.5">
                        {BRAND_NAME_MAP[product.brand] || product.brand}
                      </p>
                    </div>
                    {/* Actions */}
                    <div className="shrink-0">
                      <span
                        className={`flex items-center px-4 py-2 rounded-md font-medium text-xs transition-all border ${
                          inQuote 
                            ? 'bg-green-50 text-green-700 border-green-200' 
                            : 'bg-white border-gray-200 text-gray-700 group-hover:bg-black group-hover:text-white group-hover:border-black'
                        }`}
                      >
                        {inQuote ? '✓ Eklendi' : 'Ekle'}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
            
            {/* Minimal footer if cart has items */}
            {count > 0 && (
              <div className="bg-gray-50 px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs text-gray-500 font-medium">Sepetinizde {count} ürün var</span>
                <button 
                   onClick={() => { onClose(); openDrawer(); }}
                   className="text-xs font-semibold text-accent hover:underline"
                >
                  Teklif Sepetine Git →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
